// İSG uzmanı portalı — Risk Değerlendirme (Risk) erişim sondası (route guard UX).
//
// Asıl yetki backend'dedir: her Risk ucu `[ModuleAction("risk-assessments", …)]` taşır; grant'ı
// olmayan principal → 403 (api-contract §19). Bu sonda yalnız UX görünürlüğüdür — hafif bir liste
// probu (`GET /risk-assessments?page=1&pageSize=1`) çeker:
//   - 2xx  → erişim var (granted) → Risk kabuğu + menü görünür
//   - 403  → yetki yok (forbidden) → gizli menü + 403 sayfası
//   - diğer (404/503/ağ) → servis/veri kullanılamıyor (unavailable) → yeniden dene
//
// İş İzni (§14) / Eğitim (§16) sondalarıyla aynı desendir; iş modülü olarak AYRIDIR. Gerçek modül
// Value = `risk-assessments` (küçük harf; §19) — probun bağladığı uç `list` aksiyonudur; yetkisiz
// principal buton/route göremez.

import { ApiError, riskApi } from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { riskKeys } from './riskKeys';

export type RiskAccessStatus = 'loading' | 'granted' | 'forbidden' | 'unavailable';

export interface RiskAccess {
  status: RiskAccessStatus;
  /** `unavailable` durumunda yeniden denemek için. */
  refetch: () => void;
}

/**
 * Risk Değerlendirme erişimini backend sondasıyla belirler. Oturum açık (RequireAuth altı)
 * varsayılır. 4xx'te retry yok (anında sonuç); 403 (yetki yok) ≠ 404/503 (servis/veri) ayrımı korunur.
 */
export function useRiskAccess(): RiskAccess {
  const query = useQuery({
    queryKey: riskKeys.access(),
    queryFn: () => riskApi.listRiskAssessments({ page: 1, pageSize: 1 }),
    retry: false,
    staleTime: 5 * 60_000,
  });

  let status: RiskAccessStatus;
  if (query.isPending) {
    status = 'loading';
  } else if (query.isSuccess) {
    status = 'granted';
  } else if (query.error instanceof ApiError && query.error.statusCode === 403) {
    status = 'forbidden';
  } else {
    status = 'unavailable';
  }

  return { status, refetch: () => void query.refetch() };
}
